"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StockMovementSchema = exports.STOCK_MOVEMENT_KINDS = exports.UnitEventSchema = exports.ProvisioningTemplateSchema = exports.ProvisioningCategorySchema = exports.ProvisioningStepSchema = exports.OS_INSTALL_GATE_FROM = exports.DeviceUnitSchema = exports.DeviceUnitShipmentSchema = exports.DeviceAgreementSchema = exports.DeviceOrderSchema = exports.PendingReplacementSchema = exports.PENDING_REPLACEMENT_LANES = exports.PENDING_REPLACEMENT_OUTCOMES = exports.ShippingAddressSchema = exports.DeviceOrderItemSchema = exports.AGREEMENT_STATUSES = exports.REPLACEMENT_OUTCOME = exports.REPLACEMENT_REASONS = exports.ORDER_STATUSES = exports.UNIT_STATUSES = void 0;
exports.isInWarranty = isInWarranty;
exports.warrantyDaysLeft = warrantyDaysLeft;
exports.warrantyUntilFrom = warrantyUntilFrom;
exports.osInstallRequired = osInstallRequired;
exports.osInstallGate = osInstallGate;
exports.deriveOrderStatus = deriveOrderStatus;
exports.agreementProgress = agreementProgress;
exports.rollOverAmount = rollOverAmount;
const zod_1 = require("zod");
const documentDelta_1 = require("./documentDelta");
const hardwareCatalog_1 = require("./hardwareCatalog");
const welcome_1 = require("./welcome");
/**
 * DEVICE ORDERS — a tenant's device becomes an ORDER, which allocates UNITS
 * (serial-numbered devices), each optionally carrying a finance AGREEMENT.
 * See §7.1 / §7.2 / §7.2a / §7.5 of the onboarding-billing-devices design.
 */
// Union of the order-fulfilment pipeline (§7.2a) and the unit lifecycle
// (§7.2) — a unit's `state` is set from either vocabulary depending on where
// it is in its life: `in_stock`/`allocated` before it belongs to an order,
// the fulfilment states while an order is being worked, and
// `returned`/`rma`/`retired` after.
//
// `provisioning` and `configured` (inventory-hub-unit-lifecycle wave) sit
// between `processing` and `packing`: a unit's per-model checklist
// (DeviceUnitSchema.provisioning, snapshotted from a ProvisioningTemplateSchema
// at the moment provisioning starts) is worked through in `provisioning` and
// auto-advances to `configured` once every REQUIRED step is ticked. Packing
// reuses the EXISTING `packing` status — there is no separate "packed" state,
// same as the mockup's rail treats "Packed (box)" as this same step.
// 'lost' is deliberately NOT the same thing as 'returned'. A returned device
// is coming back and will be inspected; a lost one never arrived and starts a
// CARRIER CLAIM instead. Recording one as the other loses the claim and makes
// an inspection queue that nobody can ever clear, so they are separate states
// rather than one state with a reason field.
exports.UNIT_STATUSES = [
    'in_stock', 'allocated', 'awaiting', 'processing', 'provisioning', 'configured', 'packing', 'shipped',
    'delivered', 'deployed', 'on_hold', 'backordered', 'returned', 'rma', 'retired', 'lost',
];
// §7.1's original sketch (`pending|configuring|shipped|delivered|cancelled`)
// contradicts its own rule that order status is DERIVED from units: §7.2a
// later moved status onto the unit with a 13-value vocabulary, so an order
// with a backordered unit must be able to report 'backordered'. §7.2a is the
// later, more specific decision — it wins. ORDER_STATUSES is therefore
// EXACTLY what deriveOrderStatus can emit (all of UNIT_STATUSES) plus
// 'cancelled', which is the one status either side may set directly and
// only before anything ships (§7.1) — no unit ever derives it. 'pending' and
// 'configuring' had no writer once status moved onto the unit and are
// dropped.
exports.ORDER_STATUSES = [...exports.UNIT_STATUSES, 'cancelled'];
/**
 * WHY A DEVICE IN THE FIELD IS BEING REPLACED.
 *
 * The reason is not commentary — it decides the state the outgoing device
 * lands in, and each state starts a different real process: an inspection, a
 * carrier claim, a vendor RMA, or a write-off. Picking it is therefore a
 * decision, which is why this is a closed set rather than free text.
 */
exports.REPLACEMENT_REASONS = ['returned', 'lost_in_transit', 'warranty', 'damaged'];
/**
 * Where the outgoing device goes, per reason.
 *
 * `damaged` deliberately lands on `retired` rather than inventing a state:
 * the existing damage flow already owns write-offs and decides who the cost
 * falls on, and this must feed that rather than route around it.
 */
exports.REPLACEMENT_OUTCOME = {
    returned: 'returned',
    lost_in_transit: 'lost',
    warranty: 'rma',
    damaged: 'retired',
};
/**
 * Is this unit inside its warranty window at `now`?
 *
 * UNKNOWN IS NOT COVERED. A unit with no `warrantyUntil` — every unit
 * delivered before that field existed — returns false rather than true,
 * because the alternative is honouring claims the business never priced.
 * The caller shows "unknown", not "expired", so nobody reads a missing
 * stamp as a decision anyone made.
 */
function isInWarranty(unit, now = Date.now()) {
    return typeof unit.warrantyUntil === 'number' && unit.warrantyUntil > now;
}
/** Whole days of cover left, or null when the device has no warranty stamp. */
function warrantyDaysLeft(unit, now = Date.now()) {
    if (typeof unit.warrantyUntil !== 'number')
        return null;
    return Math.max(0, Math.ceil((unit.warrantyUntil - now) / 86400000));
}
/**
 * The warranty deadline for a device delivered at `deliveredAt`.
 *
 * Calendar months, not 30-day blocks — "12 months" on an invoice means the
 * same date next year, and a customer counting on their calendar must not be
 * told they are five days late.
 *
 * Returns undefined when the catalog states no warranty, so the field stays
 * absent rather than being stamped with a same-day expiry.
 */
function warrantyUntilFrom(deliveredAt, warrantyMonths) {
    if (typeof warrantyMonths !== 'number' || warrantyMonths <= 0)
        return undefined;
    const d = new Date(deliveredAt);
    const day = d.getUTCDate();
    d.setUTCMonth(d.getUTCMonth() + warrantyMonths);
    // 31 Jan + 1 month would roll into March; pin to the last day of the
    // intended month instead, which is what "one month later" means to a person.
    if (d.getUTCDate() < day)
        d.setUTCDate(0);
    return d.getTime();
}
// 'cancelled' — a rental ended by the operator: Stripe billing stops at
// period end and the device is expected back. Terminal, like settled/waived.
exports.AGREEMENT_STATUSES = ['active', 'settled', 'waived', 'defaulted', 'cancelled'];
exports.DeviceOrderItemSchema = zod_1.z.object({
    skuId: zod_1.z.string().min(1),
    bundleId: zod_1.z.string().optional(),
    modelId: zod_1.z.string().optional(),
    quantity: zod_1.z.number().int().positive(),
    saleMode: zod_1.z.enum(hardwareCatalog_1.SALE_MODES),
    amountCents: zod_1.z.number().int().min(0),
    termMonths: zod_1.z.number().int().positive().optional(),
});
/**
 * WHERE THE HARDWARE ACTUALLY GOES.
 *
 * An order used to carry only a `locationId`, and whoever picked it had to
 * look the location up and hope its address was both present and current. It
 * often is neither: a store being fitted out takes delivery somewhere else, and
 * a location row's address is edited long after an order shipped.
 *
 * So the address is COPIED ONTO THE ORDER at checkout — the address the
 * customer confirmed, frozen at the moment they confirmed it. Optional, because
 * every order written before this field existed has none, and those still read
 * and ship exactly as they did.
 */
exports.ShippingAddressSchema = zod_1.z.object({
    /** Who takes delivery. Falls back to the location name when not stated. */
    recipient: zod_1.z.string().optional(),
    line1: zod_1.z.string().min(1),
    line2: zod_1.z.string().optional(),
    city: zod_1.z.string().min(1),
    state: zod_1.z.string().min(1),
    postalCode: zod_1.z.string().min(1),
    country: zod_1.z.string().default('US'),
    /** For the courier, not for us. */
    phone: zod_1.z.string().optional(),
    notes: zod_1.z.string().optional(),
});
/** What happens to the device being replaced. */
exports.PENDING_REPLACEMENT_OUTCOMES = ['good', 'rma', 'destroy', 'lost'];
exports.PENDING_REPLACEMENT_LANES = ['pos', 'payment', 'other'];
/**
 * One replacement in flight on an order — see `DeviceOrderSchema.pendingReplacements`.
 * `startedAt` and `actorEmail` are stamped by the service, never the caller.
 */
exports.PendingReplacementSchema = zod_1.z.object({
    unitId: zod_1.z.string().min(1),
    outcome: zod_1.z.enum(exports.PENDING_REPLACEMENT_OUTCOMES),
    reason: zod_1.z.string().min(1),
    lane: zod_1.z.enum(exports.PENDING_REPLACEMENT_LANES),
    modelId: zod_1.z.string().optional(),
    terminalId: zod_1.z.string().optional(),
    provisionCode: zod_1.z.string().optional(),
    emvDeviceId: zod_1.z.string().optional(),
    cardTender: zod_1.z.string().optional(),
    varId: zod_1.z.string().optional(),
    applicationId: zod_1.z.string().optional(),
    partnerUnitId: zod_1.z.string().optional(),
    postDispatch: zod_1.z.boolean().optional(),
    startedAt: zod_1.z.number(),
    actorEmail: zod_1.z.string().optional(),
});
exports.DeviceOrderSchema = zod_1.z.object({
    /** Welcome-wizard unlock code state (see welcome.ts). Optional: absent on every pre-existing order. */
    welcomeLock: welcome_1.OrderWelcomeLockSchema.optional(),
    /** When the FIRST device of this order finished its welcome — set once, conditionally, so "is live" emails go once. */
    welcomeFirstActivatedAt: zod_1.z.string().optional(),
    /**
     * The number a HUMAN uses — `RB-1004382`. See orderNumber.ts.
     *
     * OPTIONAL, and never the key: `orderId` is referenced by every unit,
     * envelope, invoice and audit row, so re-keying would orphan years of
     * history. Absent on every order placed before this field existed; the
     * backfill stamps those.
     */
    orderNo: zod_1.z.string().max(32).optional(),
    orderId: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    locationId: zod_1.z.string().min(1),
    items: zod_1.z.array(exports.DeviceOrderItemSchema).min(1),
    // DERIVED from unit states via deriveOrderStatus — never set directly,
    // except 'cancelled', which either side may set and only before anything
    // ships. See §7.1 / §7.2a.
    status: zod_1.z.enum(exports.ORDER_STATUSES),
    // Set by super admin during fulfilment.
    carrier: zod_1.z.string().optional(),
    trackingNumber: zod_1.z.string().optional(),
    trackingUrl: zod_1.z.string().optional(),
    shippedAt: zod_1.z.number().optional(),
    deliveredAt: zod_1.z.number().optional(),
    unitIds: zod_1.z.array(zod_1.z.string()).default([]),
    notes: zod_1.z.string().optional(),
    /**
     * The delivery address as the customer confirmed it at checkout. ADDITIVE and
     * optional: an order written before this existed has none, and fulfilment
     * falls back to the location record exactly as it always did.
     */
    shippingAddress: exports.ShippingAddressSchema.optional(),
    // Wave 4 — device-finance agreement gate (additive). Set once, at order
    // creation, when the order contains a financed device: `envelopeId` names
    // the documents-service envelope instantiated from the offer's bound
    // template; `envelopeStatus` is a best-effort CACHE of its last-known
    // status (documents-service is the source of truth — hardware-service's
    // packing gate always re-checks live rather than trusting this field, so a
    // stale cache can never let an order through). Absent on orders with no
    // financed devices, which pass the packing gate freely.
    envelopeId: zod_1.z.string().optional(),
    envelopeStatus: zod_1.z.string().optional(),
    /*
      WHO SOLD THIS ORDER — snapshotted at checkout, never joined on read.
  
      The deal code and the affiliate behind it live on the TENANT, and the
      tenant's copy is REWRITTEN whenever the plan changes (`POST
      /onboarding/deal` rewrites dealCode, referredBy and referralCode in one
      update). Reading attribution off the tenant therefore re-attributes every
      order the customer ever placed to whichever partner sold them their LATEST
      plan — so an order sold under one code silently pays commission to another.
  
      Frozen here for the same reason the shipping address is: this is the offer
      the order was actually placed under, and a refund, a clawback or a
      commission argument years later is settled from it.
  
      Additive and optional: orders written before this field existed simply have
      no snapshot, and readers fall back to the tenant as they always did.
    */
    /** The offer code this order was sold under. */
    dealCode: zod_1.z.string().optional(),
    /** The affiliate credited for it — `referredBy` on the tenant at checkout. */
    soldByAffiliateId: zod_1.z.string().optional(),
    /** That affiliate's own referral code, as it stood at checkout. */
    soldByReferralCode: zod_1.z.string().optional(),
    /*
      WHAT THIS ORDER IS BILLED ON — the only durable record of it.
  
      An ADD-ON order (`POST /billing/my/orders`) deliberately never touches the
      location's `ONBOARDING_PROGRESS` row: that row's fingerprint guard protects
      the onboarding money path and must stay untouched. But
      `ONBOARDING_PROGRESS.subscriptionId` is also the ONLY location→subscription
      map `subscriptionSync` has, including its retired-location teardown. So an
      add-on subscription recorded nowhere else exists solely inside Stripe: when
      the merchant closes the location and the parent subscription is cancelled,
      it keeps charging their card for ever and appears on no operator screen.
  
      These fields are that record. They are written ONCE, at order creation, by
      the path that created the subscription, and are never updated afterwards
      (billing-service holds no UpdateItem grant on this table — R5).
  
      Additive and optional: every order written before they existed has none,
      and every reader falls back exactly as it did.
    */
    /**
     * The Stripe subscription this order's RECURRING lines (rental, financed
     * term, software) bill on. For an add-on order this is a subscription of its
     * OWN, separate from the location's plan subscription — cancelling the plan
     * does not cancel it, which is precisely why it has to be written down.
     * Absent on an outright-only order, and on every onboarding order (whose
     * recurring lines ride the location's own subscription).
     */
    addOnSubscriptionId: zod_1.z.string().optional(),
    /**
     * The location's EXISTING plan subscription that the add-on was sold
     * against, and whose `current_period_end` anchored it. This is the join a
     * teardown needs: cancel this subscription and `addOnSubscriptionId` is what
     * must be closed with it.
     */
    parentSubscriptionId: zod_1.z.string().optional(),
    /**
     * The one-off invoice this order's outright purchases and down payments were
     * charged on, when it had any.
     */
    invoiceId: zod_1.z.string().optional(),
    /**
     * Which flow placed this order — `'addon_order'` for `POST
     * /billing/my/orders`. Absent means onboarding, which is what every order
     * written before this field existed was.
     */
    source: zod_1.z.string().optional(),
    // inventory-hub-unit-lifecycle wave — one entry per shipping box for this
    // order (screen D "Packing & boxes"). `boxNo` is stable once assigned;
    // which units are IN a box is derived from each unit's own `boxNo`
    // (DeviceUnitSchema), never duplicated here — same "derive, don't
    // denormalize" rule §7.1 applies to order status. Absent on orders that
    // predate this field or have not started packing yet.
    boxes: zod_1.z.array(zod_1.z.object({
        boxNo: zod_1.z.number().int().positive(),
        carrier: zod_1.z.string().optional(),
        trackingNumber: zod_1.z.string().optional(),
        trackingUrl: zod_1.z.string().optional(),
        weightGrams: zod_1.z.number().int().positive().optional(),
        packedAt: zod_1.z.number().optional(),
        // WHAT THE LABEL ACTUALLY COST US, in integer cents — the real outbound
        // freight for this box, filled when the shipping label is bought.
        //
        // Until this existed the only outbound figure anywhere was the model's
        // `defaultOutboundShippingCents`, which is an ESTIMATE on the catalog and
        // moves when someone edits the model. Real freight spend was therefore not
        // reportable at all, and every "net profit per device" was short by an
        // unknown amount.
        //
        // Per BOX, not per unit, because that is how carriers charge: one label
        // covers everything in the box. Attributing it to units is the report's
        // job (split across the units in the box), never a stored per-unit figure.
        //
        // Optional and never backfilled — no label cost exists for a box already
        // shipped, so historical boxes stay absent. A report MUST treat absent as
        // "unknown", never as zero: costing an unshipped-cost box at $0 would
        // overstate profit, which is the exact failure this field exists to end.
        shippingCostCents: zod_1.z.number().int().min(0).optional(),
        // serialized-vs-quantity-tracking wave — a quantity-mode model has no
        // unit to put in a box, so its packed quantity is recorded HERE instead,
        // one entry per model, merged (never duplicated) across repeated packs
        // of the same model into the same box. A box may freely mix unit-based
        // packing (derived from unit.boxNo, unchanged) and bulkLines — "mixed
        // GRNs/boxes fine" per the same rule GRN receiving follows. Absent on
        // every box that predates this field or has never packed a bulk line.
        bulkLines: zod_1.z.array(zod_1.z.object({
            modelId: zod_1.z.string().min(1),
            qty: zod_1.z.number().int().positive(),
        })).optional(),
    })).optional(),
    /**
     * REPLACEMENTS STARTED BUT NOT FINISHED, keyed by the OLD unit's id.
     *
     * The Replace-device wizard is several steps long (pull a spare, key it,
     * couple it…). Recording the intent on the order means a half-finished
     * replacement survives a reload and is visible to whoever picks the order up
     * next. Written by `POST /lifecycle/orders/{orderId}/replacements`, removed by
     * `.../replacements/{unitId}/complete`. Additive, optional, no default:
     * absent means nothing is pending.
     */
    pendingReplacements: zod_1.z.record(zod_1.z.string(), exports.PendingReplacementSchema).optional(),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
});
exports.DeviceAgreementSchema = zod_1.z.object({
    skuId: zod_1.z.string().min(1),
    saleMode: zod_1.z.enum(hardwareCatalog_1.SALE_MODES),
    // Optional (was required): an OPEN-ENDED rental has no term — it runs
    // month to month until cancelled. Every termed agreement still carries it.
    termMonths: zod_1.z.number().int().positive().optional(),
    monthlyAmountCents: zod_1.z.number().int().min(0),
    // What the customer is contracted to pay in total.
    principalCents: zod_1.z.number().int().min(0),
    // Advanced by the invoice.paid webhook — NEVER computed from elapsed time.
    // A missed payment must not look like a paid one.
    paidCents: zod_1.z.number().int().min(0).default(0),
    // Wave 4 — companion counter to paidCents, advanced by the SAME
    // invoice.paid webhook write, in the SAME transaction. Never derived from
    // elapsed time or from paidCents/monthlyAmountCents division, for the same
    // reason paidCents itself isn't: a missed payment must not look paid.
    monthsPaid: zod_1.z.number().int().min(0).default(0),
    startedAt: zod_1.z.number(),
    endsAt: zod_1.z.number().optional(),
    stripeSubscriptionId: zod_1.z.string().optional(),
    stripeScheduleId: zod_1.z.string().optional(),
    status: zod_1.z.enum(exports.AGREEMENT_STATUSES),
    documentUrl: zod_1.z.string().optional(),
    // Set on the new agreement when an upgrade/swap rolls an outstanding
    // balance forward, so the chain is walkable. See §7.5.
    supersedesAgreementId: zod_1.z.string().optional(),
    // RENTAL FIELDS (all optional — additive). Mirror the SKU's rental terms
    // at agreement creation so the contract on the unit is self-contained.
    rentalKind: zod_1.z.enum(['open_ended', 'fixed_term']).optional(),
    minCommitmentMonths: zod_1.z.number().int().min(0).optional(),
    // Deposit HELD for this unit's rental, stamped at agreement creation.
    depositCents: zod_1.z.number().int().min(0).optional(),
    // Deposit RETURNED (or withheld: refunded < held). Recorded by the
    // return/check-in flow, never derived — absent means still held.
    depositRefundedCents: zod_1.z.number().int().min(0).optional(),
    // When the rental was cancelled (status 'cancelled'). Billing stops at the
    // Stripe period end; the device is expected back from this moment.
    cancelledAt: zod_1.z.number().optional(),
});
exports.DeviceUnitShipmentSchema = zod_1.z.object({
    carrier: zod_1.z.string().min(1),
    trackingNumber: zod_1.z.string().min(1),
    trackingUrl: zod_1.z.string().optional(),
    shippedAt: zod_1.z.number().optional(),
});
// Every state from 'shipped' onward in the fulfilment pipeline necessarily
// implies a shipment happened — 'delivered' and 'deployed' cannot be true
// without it having shipped first. Required here, not just at 'shipped',
// so the guarantee holds no matter how far along the unit is.
const SHIPMENT_REQUIRED_STATES = new Set(['shipped', 'delivered', 'deployed']);
exports.DeviceUnitSchema = zod_1.z.object({
    /** Where/how the unit was first activated by the welcome wizard. Optional. */
    activation: welcome_1.UnitActivationSchema.optional(),
    unitId: zod_1.z.string().min(1),
    serialNumber: zod_1.z.string().optional(),
    modelId: zod_1.z.string().min(1),
    state: zod_1.z.enum(exports.UNIT_STATUSES),
    // Company-unique `RTA-######`, minted once from an atomic counter at unit
    // creation (see hardware-service's mintTrackingId). Optional so units
    // created before this field existed still validate — nothing backfills
    // them. Never re-minted once stamped.
    trackingId: zod_1.z.string().optional(),
    /**
     * EXTRA BARCODES THAT RESOLVE TO THIS UNIT — aliases, never identity.
     *
     * `trackingId` above stays the one true id: allocations, orders, audit rows
     * and printed labels all point at it, and nothing here changes that. These
     * are the cheap adhesive barcodes bought by the roll and already printed
     * with a number — stuck on a device so it can be scanned today, before the
     * RTA id is printed on something durable. Several may point at one unit (a
     * label on the box, another on the base, a replacement over a peeling one),
     * which is why this is a LIST.
     *
     * THE ARRAY IS NOT THE LOOKUP. A scan resolves through a pointer row
     * (`pk = "xbc#<code>"`) whose conditional write is what guarantees one code
     * can never point at two devices; this field is the same fact stored on the
     * unit so a card can show its own labels without a second query. Both are
     * written in ONE transaction, so they cannot drift.
     *
     * Absent on every unit that predates the field, and on every unit nobody
     * has stuck a bought label on — which is not the same as having none.
     */
    externalBarcodes: zod_1.z.array(zod_1.z.string().min(1)).optional(),
    // Where this physical unit came from, stamped when it is received against
    // a GRN. Absent for units created directly (e.g. manual stock entry).
    vendorId: zod_1.z.string().optional(),
    grnId: zod_1.z.string().optional(),
    // What THIS unit cost us — the LANDED figure (unit + inbound + duty), so
    // every consumer reads one number and none of them has to do the arithmetic.
    // Falls back to the model's landedCostCents when absent (older units,
    // manual entries).
    costCents: zod_1.z.number().int().min(0).optional(),
    /**
     * How that landed figure was arrived at, when an operator entered it by hand
     * during receiving.
     *
     * ADDITIVE and optional: every unit written before this has none, and the
     * flat `costCents` above stays the field consumers read. This exists so the
     * cost editor can REOPEN with the same three numbers rather than showing a
     * total someone has to reverse-engineer — the same model bought from a second
     * supplier differs in the shipping line, not the unit price, and that is
     * exactly what an operator needs to see next time.
     *
     * Outbound shipping is deliberately absent: it is per SHIPMENT, not per unit,
     * which is why `landedCostCents()` excludes it too.
     */
    costBreakdown: zod_1.z.object({
        unitCostCents: zod_1.z.number().int().min(0),
        inboundShippingCents: zod_1.z.number().int().min(0),
        dutyCents: zod_1.z.number().int().min(0),
    }).optional(),
    tenantId: zod_1.z.string().optional(),
    locationId: zod_1.z.string().optional(),
    terminalId: zod_1.z.string().optional(),
    /**
     * THE OTHER HALF OF THE PAIR.
     *
     * One POS device to one payment device, per location: the till carries its
     * reader's unitId and the reader carries the till's, so scanning either
     * barcode finds the other without a second table or a join. Written on BOTH
     * units in the same breath — a one-sided link is how half a pair leaves in
     * a box on its own, which is the thing this field exists to prevent.
     *
     * An all-in-one (till and reader in one shell) points at itself.
     *
     * Optional and absent on every unit that predates it; nothing backfills.
     */
    coupledUnitId: zod_1.z.string().optional(),
    /**
     * WHAT THE REGISTER WILL BE CALLED, typed before the register exists.
     *
     * The name is chosen at the bench, minutes or hours before anyone presses
     * Create register — and until now it lived in React state, so a refresh, a
     * tab change or a dropped connection silently threw it away and the next
     * person got the model name back. It is a draft, so it belongs on the unit
     * it describes rather than nowhere.
     *
     * Only a DEFAULT: `createRegister` sends the name explicitly, and the
     * register's own name is authoritative once it exists. Nothing reads this
     * afterwards, and nothing keeps the two in step on purpose — renaming a
     * live register is that register's business, not this field's.
     */
    registerName: zod_1.z.string().optional(),
    orderId: zod_1.z.string().optional(),
    agreement: exports.DeviceAgreementSchema.optional(),
    shipment: exports.DeviceUnitShipmentSchema.optional(),
    // Required when state is 'on_hold' or 'backordered' and shown to the
    // customer — a device silently stuck in a warehouse is exactly the
    // experience this guard prevents. See §7.2a.
    holdReason: zod_1.z.string().optional(),
    // Set when this unit's rental is cancelled: the device is out at the
    // tenant and expected back. Cleared by check-in (state 'returned' →
    // inspection → 'in_stock'). Absent on every unit not awaiting a return.
    returnExpectedAt: zod_1.z.number().optional(),
    /**
     * WHEN THIS DEVICE WENT INTO SERVICE — the warranty clock's zero.
     *
     * Stamped once, on the transition into `delivered`, and never moved
     * afterwards: a device that is later reinstalled, re-provisioned or swapped
     * onto another register has still been in the customer's hands since this
     * date, and that is what a warranty counts.
     *
     * THIS CANNOT BE BACKFILLED. Rental months are advanced by invoice
     * webhooks, never by elapsed time, and no timeline event is written on a
     * state change — so for a device delivered before this field existed there
     * is no record anywhere of when it arrived. Absent means exactly that:
     * unknown, treated as out of warranty rather than guessed.
     */
    deliveredAt: zod_1.z.number().optional(),
    /**
     * `deliveredAt` + the model's or SKU's `warrantyMonths`, computed once at
     * delivery and STORED rather than derived on read.
     *
     * Stored because the term is a property of the deal that was struck: a
     * catalog edit that changes `warrantyMonths` next year must not silently
     * retract cover from devices already in the field, which is exactly what
     * deriving it on every read would do.
     */
    warrantyUntil: zod_1.z.number().optional(),
    /**
     * WHICH DEVICE THIS ONE WAS SENT OUT TO REPLACE.
     *
     * The order still promises the customer the same number of devices; this
     * records that a different physical box is now meeting that promise, and
     * why. Without it the only trace of a swap is two units sharing an orderId
     * with no way to tell which superseded which.
     */
    replaces: zod_1.z.object({
        unitId: zod_1.z.string().min(1),
        reason: zod_1.z.enum(exports.REPLACEMENT_REASONS),
        at: zod_1.z.number(),
        by: zod_1.z.string().optional(),
    }).optional(),
    /** The other side of `replaces`, written on the device being replaced. */
    replacedByUnitId: zod_1.z.string().optional(),
    notes: zod_1.z.string().optional(),
    /**
     * The same note as rich text, as a Quill Delta.
     *
     * DELTA, NOT HTML, and that is a security decision rather than a stylistic
     * one: this app ships no sanitiser, so storing markup an operator authored
     * and rendering it back would be a stored-XSS path — narrow, since both
     * author and reader hold admin-pool tokens, but an escalation route inside
     * an admin tool all the same. A Delta is data; there is nothing to inject.
     *
     * `notes` above stays the PLAIN TEXT of the same content and remains the
     * field every existing consumer reads — exports, list rows, the note pill.
     * Nothing has to learn about Deltas to keep working.
     */
    notesRich: documentDelta_1.DeltaDocumentSchema.optional(),
    /**
     * Condition and QA badges currently on this unit, by id.
     *
     * A tolerant `string[]`, deliberately not an enum of UNIT_BADGES: retiring a
     * badge from the catalogue must never invalidate a unit that already carries
     * it, because that row records something a person actually observed. The
     * label lookup degrades to the raw id.
     *
     * This is CURRENT STATE only. Every apply and remove also writes an audit row
     * on the unit, which is where the timestamped history lives.
     */
    badges: zod_1.z.array(zod_1.z.string()).optional(),
    // inventory-hub-unit-lifecycle wave (screen B "Identity & provenance") —
    // additive identity fields, editable through POST /units same as serial.
    mac: zod_1.z.string().optional(),
    firmwareNotes: zod_1.z.string().optional(),
    // inventory-hub-unit-lifecycle wave, B2 (timeline/events/firmware
    // tracking) — additive device-health fields, editable through POST /units
    // the same way mac/firmwareNotes are. hardware-service auto-appends a
    // SYSTEM timeline event ("OS installed" / "OS version → X", "Build X.Y
    // installed") whenever osVersion or appBuild CHANGES, so firmware history
    // is never silent — see UnitEventSchema below. lastSeenAt is a heartbeat
    // timestamp set the same way. All three are absent for a unit that
    // predates this field or has never reported in — nothing backfills them.
    osVersion: zod_1.z.string().optional(),
    appBuild: zod_1.z.string().optional(),
    lastSeenAt: zod_1.z.number().optional(),
    /**
     * WHAT THE MACHINE SAYS IT IS — read off the device over adb by
     * `install-device.sh`, never typed.
     *
     * The unit record carried "dfgdfgdfgdg" as an app build and "dfgdsfg" as an
     * OS version, because those were free-text boxes on a form. Anything the
     * hardware can answer for itself should not be a question on a form at all,
     * and the fields that stay editable are the ones only a person knows.
     *
     * EVERY FIELD IS OPTIONAL AND ALWAYS WILL BE. This is written by a probe
     * that runs against real hardware over a flaky USB or Wi-Fi link, on device
     * builds that differ in which `getprop` keys and `/sys` paths exist. A probe
     * that half-succeeds must record the half it got — a missing RAM figure must
     * never discard a MAC address that WAS read. So absence means "this run did
     * not learn it", never "the device does not have it", and no reader may
     * treat a gap as a fault.
     *
     * `capturedAt` is what makes the rest trustworthy: a spec block with no
     * timestamp cannot be told apart from one captured against a device that has
     * since been re-imaged or swapped.
     */
    hardware: zod_1.z.object({
        /** Epoch ms of the probe that wrote this block. */
        capturedAt: zod_1.z.number().int().nonnegative(),
        /** `ro.product.model` / `ro.product.brand` — the mismatch check's input. */
        model: zod_1.z.string().max(128).optional(),
        brand: zod_1.z.string().max(128).optional(),
        /** `ro.build.version.release` and the numeric SDK level beside it. */
        osVersion: zod_1.z.string().max(64).optional(),
        sdkInt: zod_1.z.number().int().nonnegative().optional(),
        /** `ro.product.cpu.abi` + core count + max clock, as the device reports them. */
        cpuAbi: zod_1.z.string().max(64).optional(),
        /**
         * `ro.build.fingerprint` — the STRONG identifier.
         *
         * Verified on a K10: `ro.product.model` is `rk3568_r` and
         * `ro.product.brand` is `rockchip` — the SoC REFERENCE BOARD, because the
         * ODM never rebranded the props. Nothing on that device says "TCANG" or
         * "TC-TOUCH-K10" anywhere in `getprop`. Those two fields alone therefore
         * cannot tell one white-label RK3568 apart from another; the fingerprint
         * carries the ODM's own build id and date and can.
         */
        fingerprint: zod_1.z.string().max(256).optional(),
        cpuModel: zod_1.z.string().max(128).optional(),
        cpuCores: zod_1.z.number().int().nonnegative().optional(),
        cpuMaxMhz: zod_1.z.number().int().nonnegative().optional(),
        /** MemTotal from /proc/meminfo, and the /data partition, both in MB. */
        ramMb: zod_1.z.number().int().nonnegative().optional(),
        storageTotalMb: zod_1.z.number().int().nonnegative().optional(),
        storageFreeMb: zod_1.z.number().int().nonnegative().optional(),
        /**
         * Wi-Fi and wired MACs SEPARATELY. One `mac` field could not say which
         * interface it came from, so a device with both reported whichever the
         * probe happened to read first and two units looked different when they
         * were not.
         */
        wifiMac: zod_1.z.string().max(64).optional(),
        ethernetMac: zod_1.z.string().max(64).optional(),
        /** How many displays Android reports — 2 on a dual-screen till. */
        displayCount: zod_1.z.number().int().nonnegative().optional(),
        /**
         * USB peripherals the device can currently see, as `VID:PID name`.
         *
         * A SNAPSHOT of one moment on a bench, not an inventory: a printer that
         * was powered off during the probe is simply absent. It answers "what was
         * plugged in when we set this up", which is the question asked when a
         * merchant later says a peripheral stopped working.
         */
        usbPeripherals: zod_1.z.array(zod_1.z.string().max(256)).max(64).optional(),
        /**
         * IS THIS TILL MANAGED? — `dpm` named our package as Device Owner.
         *
         * 🔴 THE ONE FACT ABOUT A TILL THAT CANNOT BE FIXED LATER. Device Owner can
         * only be set on a device with no accounts that has not finished setup, and
         * can only be removed by a factory reset. A unit that shipped without it
         * can never be remotely restarted, locked down or wiped, and the only way
         * to enrol it afterwards is to erase it. So the answer has to be recorded
         * at install time, on the unit, where it can be read back months later.
         *
         * OPTIONAL, AND THE ABSENCE MEANS SOMETHING DIFFERENT FROM `false`.
         * Absent = nobody asked (an installer predating the probe, or a run that
         * could not reach `dumpsys`). `false` = we asked and the device said no.
         * Collapsing the two would turn every unit installed before today into a
         * fleet of tills that look like they lost their enrolment.
         *
         * Declared here because `hardware` is a plain `z.object`, and Zod STRIPS
         * unknown keys on parse — a writer sending this field without this line
         * would have it silently dropped on the way into DynamoDB, which is the
         * worst shape of failure: the panel shows it, the record never holds it.
         */
        deviceOwner: zod_1.z.boolean().optional(),
        /** The package `dumpsys` named as owner — ours, or somebody else's. */
        deviceOwnerPackage: zod_1.z.string().max(256).optional(),
    }).optional(),
    /**
     * THE VERIFIED OS INSTALL — the stamp the installer leaves behind.
     *
     * NOT `appBuild`, and the difference is the whole point. `osVersion` and
     * `appBuild` above are un-pinned on purpose: POST /units is the door both
     * device tooling AND an admin typing into "Identity & provenance" use, so
     * either can hold any string somebody felt like entering. That is fine for
     * a health field and useless as a gate — a checklist unlocked by a typed
     * version is unlocked by anyone.
     *
     * This one is SERVER OWNED, same rule as boxNo and provisioning: only
     * POST /lifecycle/units/{unitId}/os-install writes it, only after the
     * installer reported a real `✓ version:` on a run that exited 0, and the
     * general upsert pins it so no round-trip through POST /units can invent
     * or revert one. It carries what landed (`appBuild`), where it came from
     * (`source` — the stage) and WHICH artifact (`target` plus `hash`/
     * `versionCode`), so "which binary is actually on that till" is answerable
     * from the record rather than from somebody's memory of a button press.
     *
     * Absent on every unit that predates this field and on every device nobody
     * has installed yet — said as "not installed", never assumed.
     */
    osInstall: zod_1.z.object({
        /** What the installer itself reported landing — never typed by hand. */
        appBuild: zod_1.z.string().min(1),
        /** The STAGE it was injected from. `local` compiles from a working tree. */
        source: zod_1.z.enum(['local', 'dev', 'prod']),
        /** The build target, i.e. which batch of binary this is. */
        target: zod_1.z.string().min(1),
        /** The published artifact's own identity, when the source published one.
         *  Absent for `local`, which builds from source and publishes nothing. */
        versionCode: zod_1.z.number().int().optional(),
        hash: zod_1.z.string().optional(),
        /** What adb reported it was talking to — a hardware serial, or a Wi-Fi
         *  connection address when the install went over the network. */
        serial: zod_1.z.string().optional(),
        /**
         * Whether the device's app data was WIPED as part of this install.
         *
         * `adb install -r` keeps it, so a reinstall alone leaves the previous
         * pairing (terminalId, tenant, location) sitting in the app's storage and
         * the till comes back believing it still belongs to its last shop. The
         * installer clears it by default and reports whether that actually took —
         * this records the answer, because "installed" and "blank" are different
         * claims and only one of them means the device can be sent anywhere.
         *
         * Absent on stamps written before this field existed; `false` means the
         * install deliberately kept the data.
         */
        reset: zod_1.z.boolean().optional(),
        installedAt: zod_1.z.number(),
        by: zod_1.z.string(),
    }).optional(),
    /**
     * SOMEBODY ASKED FOR THIS TILL TO BE BUILT AGAIN.
     *
     * A device whose OS install turned out wrong — bad build, wrong channel,
     * data that should have been wiped and was not — has to go back through
     * Pending Configuration and be installed again. That is not a state on its
     * own: the unit returns to `allocated` and re-queues like any other. This
     * records WHY it went back and WHO sent it, because a unit that has been
     * round the loop twice looks identical to one that has been round once.
     *
     * `osInstall` is deliberately NOT cleared. It is the record of the install
     * that is actually on the device right now, and "the last install we know
     * about" stays true until a new one replaces it. Which of the two is newer
     * is what `osInstallGate` compares — a request newer than the install means
     * the reinstall has not happened yet, and the checklist stays locked.
     *
     * Before dispatch only. The OS install runs over local adb (USB, or the same
     * network), so a device that has left the building cannot be reinstalled
     * from here at all and the endpoint refuses rather than queueing something
     * nobody can carry out.
     */
    reinstall: zod_1.z.object({
        requestedAt: zod_1.z.number(),
        requestedBy: zod_1.z.string(),
        /** Free text from the operator. Shown in the queue so the next person
         *  knows what to do differently. */
        reason: zod_1.z.string().optional(),
        /** Where the unit was sent back FROM, which the state no longer says. */
        fromState: zod_1.z.string(),
    }).optional(),
    /**
     * THE RECEIPTS THIS PAIR PRINTED — the proof a till and its reader work.
     *
     * A test sale is run on a COUPLE: the till rings it, the reader takes the
     * card, and the receipt that falls out is evidence about both machines at
     * once. It is recorded on the POS unit because that is what identifies the
     * pair (`coupledUnitId` points from it to the reader), so one device holds
     * the pair's proof rather than two half-copies that can disagree.
     *
     * WHY THIS IS NOT A JOURNEY MARK. The order journey carries ONE manual
     * completion per stage, with one attachment — fine for "this order was
     * tested", useless for an order with four pairs. Four pairs are four
     * separate proofs, and a single mark meant the second upload silently
     * replaced the first. The stage mark is still written (it is what
     * `receipt_printing` resolves from, and what gates dispatch); these are the
     * evidence behind it.
     *
     * MANY PER KIND, deliberately. One photo rarely shows a whole receipt —
     * the header, the card line and the signature are often three shots — and
     * asking somebody to pick the single best one is how proof gets thinner.
     *
     * SERVER OWNED, like `provisioning` and `boxNo`: only the dedicated
     * receipts endpoints write it, never the general unit upsert. Evidence that
     * a stale full-object round-trip could blank is not evidence.
     *
     * Absent on every unit that predates this field and on every pair nobody has
     * tested yet — said as "no receipts", never as a failed test.
     */
    receipts: zod_1.z.array(zod_1.z.object({
        /** Stable id, so one photo can be removed without touching the rest. */
        receiptId: zod_1.z.string().min(1),
        /**
         * Which proof this is. Each kind is counted and gated SEPARATELY — every
         * reader filters by kind rather than counting the array — so a new kind
         * can be added without changing what the existing two mean.
         *
         * `label` is the odd one out and is deliberately here rather than in an
         * array of its own: it is the photograph of the STICKER on the device —
         * serial, FCC ID, the MACs, whatever else the manufacturer printed. A
         * terminal's label often carries several numbers, and which of them
         * matters is not always known at receiving time, so the photo is captured
         * whole instead of being transcribed into fields that would miss one.
         * Reusing this array gets the presign, the append-only write, the
         * conditional update, the timeline row and the delete for free.
         */
        kind: zod_1.z.enum(['test', 'refund', 'label']),
        /** The S3 key. A URL is never stored — links are presigned on read. */
        key: zod_1.z.string().min(1),
        /**
         * THE EXCEPTION TO THE LINE ABOVE, and only for `kind: 'label'`.
         *
         * A test/refund receipt is uploaded through uploads-service into the
         * TENANT's bucket, so its link has to be presigned per view — hence `key`
         * alone. A label photo is taken at RECEIVING, before a unit has any
         * tenant, so it goes through hardware-service's own `/photos/presign`
         * (uploads-service is gated by the tenant Cognito pool and 401s for
         * super-admin). That door hands back a durable `fileUrl` — the same kind
         * of URL `DeviceModelSchema.media.photos` already stores — and there is no
         * per-tenant presign to re-derive it from. Kept optional so every existing
         * receipt, which has no URL and needs none, still validates.
         */
        url: zod_1.z.string().optional(),
        /** The file's own name, for the operator to recognise it by. */
        name: zod_1.z.string().optional(),
        contentType: zod_1.z.string().optional(),
        at: zod_1.z.number(),
        by: zod_1.z.string(),
    })).optional(),
    // Which box (DeviceOrderSchema.boxes[].boxNo) this unit was packed into.
    // A unit is in exactly one box at a time — re-packing moves it. SERVER
    // owned: only the packing endpoint sets it, never a general unit upsert
    // (same "no side door" reasoning as agreement.status).
    boxNo: zod_1.z.number().int().positive().optional(),
    // The provisioning checklist this SPECIFIC unit is working through —
    // snapshotted from the model's ProvisioningTemplateSchema the moment
    // provisioning starts, so editing the template later never rewrites a
    // machine already in flight (mockup screen E). Absent for a unit that has
    // never started provisioning (including every unit that predates this
    // field) — the packing gate treats a missing snapshot as "nothing to
    // check", so a model with no template never bricks its units. SERVER
    // owned, same as boxNo: only the dedicated provisioning endpoints write it.
    provisioning: zod_1.z.object({
        modelId: zod_1.z.string().min(1),
        startedAt: zod_1.z.number(),
        /**
         * WHICH VERSION OF THE PLAN this unit was worked against.
         *
         * The steps are already snapshotted, so changing a model's template never
         * alters a unit already in progress — that part was right. What was
         * missing is being able to SAY which version it followed: without it, a
         * device that failed in the field cannot be traced to the checklist that
         * was actually used, and "we changed that step in September" becomes
         * unanswerable.
         *
         * The template's own `updatedAt` is its version marker. Absent on units
         * provisioned before this field existed, and on a synthesized plan.
         */
        templateUpdatedAt: zod_1.z.number().optional(),
        /**
         * True when no template was saved for the model and one was synthesized.
         *
         * Worth recording: a synthesized plan is not a plan somebody wrote, and a
         * unit that passed provisioning against nothing should be visibly
         * different from one that passed a real checklist.
         */
        templateSynthesized: zod_1.z.boolean().optional(),
        steps: zod_1.z.array(zod_1.z.object({
            stepId: zod_1.z.string().min(1),
            label: zod_1.z.string().min(1),
            // The category's LABEL at snapshot time (not categoryId) — the
            // checklist groups by this text and must keep reading correctly even
            // if the template's category is later renamed or removed.
            category: zod_1.z.string(),
            required: zod_1.z.boolean(),
            done: zod_1.z.boolean(),
            by: zod_1.z.string().optional(),
            at: zod_1.z.number().optional(),
            /**
             * The template step's instructions AS THEY READ when provisioning
             * started. Snapshotted for the same reason the label and category are:
             * the person working this unit must see the procedure that applied to
             * it, not one edited afterwards. Absent for steps with no instructions
             * and for every unit snapshotted before this field existed.
             */
            instructions: zod_1.z.string().optional(),
        })),
    }).optional(),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
}).refine((u) => (u.state !== 'on_hold' && u.state !== 'backordered') || !!u.holdReason, { message: 'holdReason is required when a unit is on_hold or backordered', path: ['holdReason'] }).refine(
// A shipped status with nowhere to track is worse than no status at all —
// and 'delivered'/'deployed' necessarily happened after shipping (they sit
// strictly later in the fulfilment pipeline, see STATUS_RANK below), so
// they carry the same requirement one step further down the line.
(u) => !SHIPMENT_REQUIRED_STATES.has(u.state) || !!(u.shipment?.carrier && u.shipment?.trackingNumber), { message: 'shipment.carrier and shipment.trackingNumber are required once a unit has shipped', path: ['shipment'] });
/**
 * WHEN THE OS GATE STARTED APPLYING.
 *
 * A unit already part-way through its checklist when this shipped has no
 * `osInstall` stamp and never could have — the endpoint that writes one did
 * not exist while it was being worked. Gating it would freeze a device
 * mid-provisioning on a warehouse bench, which is breaking work that works;
 * the rule is for machines starting from here.
 *
 * Deliberately generous: a unit whose plan was pinned at any point before
 * this date is grandfathered, so the boundary can only ever let an older
 * device through, never trap one. New units carry no `provisioning.startedAt`
 * until their first tick, and `osInstallGate` treats that as gated.
 */
exports.OS_INSTALL_GATE_FROM = Date.UTC(2026, 8, 10); // 2026-09-10
/**
 * MUST THIS DEVICE HAVE THE OS ON IT BEFORE ITS CHECKLIST CAN BE TICKED?
 *
 * Only the tills. A `pos_terminal` is an Android machine whose checklist is
 * almost entirely things you cannot do until it has booted the right build —
 * "Update and Download Parameters", "Test Manual Card Transaction" — so
 * ticking those against a device with no OS on it is recording work that did
 * not happen.
 *
 * A card reader's firmware does not come from us, and a cash drawer has no
 * OS at all, so neither is gated: the rule would only ever be an obstacle.
 */
function osInstallRequired(model) {
    return (0, hardwareCatalog_1.deviceLane)(model) === 'pos';
}
/**
 * WHY A TICK IS REFUSED, or undefined when it is allowed.
 *
 * ONE function, shared by the service that enforces this and the panel that
 * draws it, for the same reason `deviceLane` is shared: a gate the screen
 * draws differently from the way the server applies it is a gate that teaches
 * people the screen is lying. The service calls it to refuse; the panel calls
 * it to lock the rows and say what is missing.
 */
function osInstallGate(args) {
    if (!osInstallRequired(args.model))
        return undefined;
    /*
      A REINSTALL REQUEST RE-CLOSES THIS GATE, and it is checked first.
  
      The old `osInstall` stamp is still there — it describes the build actually
      sitting on the device — so the truthiness test below would wave the unit
      straight back through and let somebody tick a checklist against the very
      install they just asked to be redone. The comparison, not the presence, is
      what says whether the work has been done: a request newer than the install
      means it has not.
  
      This also beats the grandfather clause underneath. That clause exists so an
      old device already mid-provisioning is not trapped; a reinstall asked for
      today is neither old nor accidental.
    */
    const requestedAt = args.unit.reinstall?.requestedAt;
    if (typeof requestedAt === 'number' && requestedAt > (args.unit.osInstall?.installedAt ?? 0)) {
        return 'a reinstall was requested for this till — install RubyTill again from Device '
            + 'Management, then its checklist unlocks';
    }
    if (args.unit.osInstall)
        return undefined;
    const startedAt = args.unit.provisioning?.startedAt;
    if (typeof startedAt === 'number' && startedAt < exports.OS_INSTALL_GATE_FROM)
        return undefined;
    /*
     * "RubyTill", not "OS". Nothing here installs an operating system — the
     * device ships with Android already on it and the installer pushes the
     * RubyTill APK over adb. The stored field stays `osInstall` because
     * renaming a live field breaks every reader, but what a person is told has
     * to match what actually happens, or they go looking for an OS image.
     */
    return 'this till has no verified RubyTill install — install it from Device Management first, '
        + 'then its checklist unlocks';
}
// Every UNIT_STATUS gets an explicit, distinct rank — the lowest wins as the
// order's reported status. `Record<UnitStatus, number>` (not `Partial`) is
// deliberate: it forces a compile error if UNIT_STATUSES ever grows without
// this map being updated, so the exhaustiveness this function depends on
// can't silently rot.
//
// The six FULFILMENT states rank 0-5 in pipeline order (awaiting < ... <
// deployed). Everything else is an EXCEPTION and always outranks (reports
// over) any fulfilment state — that half of the ordering is what makes
// "never claim more progress than the truth" hold. Because every exception
// also needs its own distinct rank (two different exceptions on the same
// order must resolve to the same winner regardless of which unit happens to
// come first in the array — see the tie-break tests), they are ordered by
// how much they need someone's attention, most urgent first: `on_hold` is a
// decision blocking the order; `backordered` is a known, tracked stock
// issue; `returned`/`rma`/`retired` are things already happening to a unit
// that already shipped; `allocated`/`in_stock` are states that should not
// normally appear once a unit belongs to an order at all.
const STATUS_RANK = {
    on_hold: -8,
    backordered: -7,
    returned: -6,
    rma: -5,
    retired: -4,
    // Least urgent of the dead states: a lost device is resolved by a carrier
    // claim and a replacement, not by anyone handling the device itself.
    lost: -3,
    allocated: -2,
    in_stock: -1,
    awaiting: 0,
    processing: 1,
    // provisioning/configured slot in between processing and packing — the
    // per-model checklist stage the inventory-hub-unit-lifecycle wave adds.
    provisioning: 2,
    configured: 3,
    packing: 4,
    shipped: 5,
    delivered: 6,
    deployed: 7,
};
/**
 * An order's status is DERIVED from its units, never set directly. An order
 * of three devices where two are packed and one is backordered is not
 * "packing" — it reports the least-advanced unit plus a `partial` flag, so
 * nobody is told their kit is on the way when part of it is not. See §7.1.
 *
 * Every UNIT_STATUS has a distinct rank in STATUS_RANK, so the result never
 * depends on the order the units happen to be listed in.
 */
/**
 * A unit that has been written off is no longer part of what the order is
 * still delivering.
 *
 * These are the same states `allocateOrder` already discounts when it decides
 * an order is short and tops it up from stock — so a replaced device stops
 * counting for allocation but used to keep counting for STATUS, which is the
 * contradiction this set resolves.
 */
const DEAD_UNIT_STATES = new Set(['returned', 'rma', 'retired', 'lost']);
function deriveOrderStatus(unitStates) {
    if (unitStates.length === 0)
        return { status: 'awaiting', partial: false };
    /*
     * REPLACING A DEVICE MUST NOT UN-DELIVER THE ORDER.
     *
     * Status is the least-advanced unit, and a written-off unit ranks below
     * everything. So a delivered order with one lost device and one replacement
     * on its way used to derive as `lost` and `syncOrderStatus` PERSISTED that
     * regression — the merchant's own /my-devices screen then told them an
     * order they were holding had gone backwards.
     *
     * Dead units are therefore excluded, but ONLY while a live unit remains:
     * an order whose every device is written off genuinely IS `returned` or
     * `lost`, and must still say so rather than falling back to a live-sounding
     * status it no longer has any unit to justify.
     *
     * `partial` is deliberately still computed over ALL states, dead ones
     * included — a mixed order is mixed, and that flag is what tells the reader
     * the single status word is not the whole story.
     */
    const live = unitStates.filter((s) => !DEAD_UNIT_STATES.has(s));
    const considered = live.length > 0 ? live : unitStates;
    let worst = considered[0];
    for (const state of considered) {
        if (STATUS_RANK[state] < STATUS_RANK[worst])
            worst = state;
    }
    return { status: worst, partial: new Set(unitStates).size > 1 };
}
/**
 * Percentage paid is paidCents / principalCents — never computed from
 * elapsed time. A missed payment must not look like a paid one.
 */
function agreementProgress(a) {
    if (a.principalCents <= 0)
        return 0;
    return Math.max(0, Math.min(1, a.paidCents / a.principalCents));
}
/**
 * The outstanding balance (principalCents - paidCents) carried into a new
 * agreement when a device is upgraded. Never negative. See §7.5.
 */
function rollOverAmount(a) {
    return Math.max(0, a.principalCents - a.paidCents);
}
/**
 * PROVISIONING TEMPLATE — per-model, per-category checklist of what must
 * happen to a machine before it can pack (mockup screen E). One row per
 * model (hardware-service stores it pk=<modelId> sk='PROVTPL' in the
 * hardware-models table — no new table). A unit SNAPSHOTS this the moment
 * provisioning starts (see DeviceUnitSchema.provisioning); editing a
 * template afterwards never rewrites a machine already in flight — only the
 * next unit to start provisioning picks up the change.
 */
exports.ProvisioningStepSchema = zod_1.z.object({
    stepId: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1),
    // References categories[].id on the SAME template. Not validated as a
    // foreign key here (Zod schemas don't cross-reference sibling array
    // entries) — hardware-service's handler is responsible for rejecting a
    // step whose categoryId names no category on the same PUT.
    categoryId: zod_1.z.string().min(1),
    // Whether the packing gate refuses a unit until this step is done.
    // Defaults true — an informational step is the opt-out, not the default.
    required: zod_1.z.boolean().default(true),
    order: zod_1.z.number().int().default(100),
    /**
     * FREE-TEXT INSTRUCTIONS for whoever works this step (2026-08-27).
     *
     * A step label is a reminder for someone who already knows the job; this is
     * for someone who does not. "Flash the OTA channel" means nothing to a new
     * hire, and the knowledge otherwise lives only in the head of whoever set
     * the line up.
     *
     * SNAPSHOTTED onto the unit alongside the label when provisioning starts
     * (see DeviceUnitSchema.provisioning.steps) — the person ticking the box is
     * looking at the unit, not at this template, so instructions that stayed
     * here would never be read. Optional: every step written before this field
     * simply has none.
     */
    instructions: zod_1.z.string().optional(),
});
exports.ProvisioningCategorySchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1),
    order: zod_1.z.number().int().default(100),
});
exports.ProvisioningTemplateSchema = zod_1.z.object({
    modelId: zod_1.z.string().min(1),
    categories: zod_1.z.array(exports.ProvisioningCategorySchema).default([]),
    steps: zod_1.z.array(exports.ProvisioningStepSchema).default([]),
    updatedAt: zod_1.z.number(),
    updatedBy: zod_1.z.string().optional(),
});
/**
 * UNIT TIMELINE EVENT — inventory-hub-unit-lifecycle wave, B2. The
 * normalized shape `GET /lifecycle/units/{unitId}/timeline` returns for
 * EVERY entry, whether it was synthesized ("received"), derived from an
 * existing AUDIT#/DMG# row (provisioning ticks, box packs, agreement
 * actions, damage), or explicitly appended through `POST
 * /lifecycle/units/{unitId}/events` (admin, Cognito) or `POST
 * /internal/unit-events` (service-to-service, x-internal-api-key) — the
 * body shape both of those accept, minus `at` (server-stamped).
 *
 * Stored on the unit's OWN existing `AUDIT#<ts>` row idiom
 * (hardware-service's fulfilment.ts/lifecycle.ts) — this schema describes
 * the API's read/write SHAPE, not a second history store or a new table.
 *
 * `source` distinguishes who/what produced the entry: `'system'` — injected
 * automatically by tooling (OS installs, firmware/build changes,
 * provisioning auto-transitions); `'admin'` — a person acting through the
 * Cognito-authenticated admin UI; `'device'` — the unit itself reporting in
 * (e.g. a heartbeat or self-test), via the internal door.
 */
exports.UnitEventSchema = zod_1.z.object({
    unitId: zod_1.z.string().min(1),
    at: zod_1.z.number(),
    kind: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1),
    source: zod_1.z.enum(['system', 'admin', 'device']),
    actor: zod_1.z.string().optional(),
    meta: zod_1.z.record(zod_1.z.any()).optional(),
});
/**
 * STOCK MOVEMENT — serialized-vs-quantity-tracking wave. A 'quantity' model
 * (DeviceModelSchema.trackingMode) has no unit rows at all: on-hand/reserved
 * are DERIVED by summing this ledger, never a stored counter (mockup screen
 * 4: "On-hand = the ledger added up. It can't be edited directly.").
 * hardware-service's `lib/stock.ts` `recordMovement()` is the ONLY writer —
 * every row on the wire went through it.
 *
 * SIGNED-QTY CONVENTION (enforced by `recordMovement`, one place, not
 * repeated at every call site):
 *   - `received`  — always POSITIVE.  Stock physically arrived.
 *   - `adjustment`— SIGNED by the caller (a recount can go either way).
 *   - `packed`    — always NEGATIVE. Stock physically left (into a box).
 *   - `damaged`   — always NEGATIVE. Stock physically lost.
 *   - `reserved`  — always POSITIVE. An amount earmarked for an order.
 *   - `released`  — always POSITIVE. Un-earmarks a prior `reserved` amount
 *     (a pack consuming a reservation writes a `released` row for the same
 *     qty it just packed, so the reservation isn't double-counted — see
 *     hardware-service's packing endpoint doc).
 *
 * Two independent sums are taken over this one ledger, never a third stored
 * field:
 *   onHand   = Σ qty over {received, adjustment, packed, damaged}
 *   reserved = Σ qty over {reserved} − Σ qty over {released}   (floored at 0)
 *   available = onHand − reserved
 *
 * `reserved`/`released` are deliberately EXCLUDED from onHand — earmarking
 * stock for an order doesn't remove it from the warehouse, only from what's
 * free to promise elsewhere.
 */
exports.STOCK_MOVEMENT_KINDS = ['received', 'adjustment', 'reserved', 'released', 'packed', 'damaged'];
exports.StockMovementSchema = zod_1.z.object({
    modelId: zod_1.z.string().min(1),
    at: zod_1.z.number(),
    kind: zod_1.z.enum(exports.STOCK_MOVEMENT_KINDS),
    // Signed per the convention above; zero is never a valid movement (nothing
    // moved).
    qty: zod_1.z.number().int().refine((v) => v !== 0, { message: 'qty must be a non-zero integer' }),
    reason: zod_1.z.string().optional(),
    note: zod_1.z.string().optional(),
    grnId: zod_1.z.string().optional(),
    orderId: zod_1.z.string().optional(),
    boxNo: zod_1.z.number().int().positive().optional(),
    unitCostCents: zod_1.z.number().int().min(0).optional(),
    actor: zod_1.z.string().optional(),
});
//# sourceMappingURL=deviceOrders.js.map